import { useEffect, useRef, useState } from "react";

import { useDialogs } from "@/components/shell/Dialogs";
import { useMenu } from "@/components/shell/Menu";
import type { MenuItemSpec } from "@/components/shell/menuSpec";
import { hasSelectionIn, shouldOpenContextMenu } from "@/lib/contextMenu";
import { TopBar, TopBarDivider } from "@/components/shell/TopBar";
import type { ReadingFolder, ReadingLog } from "@/lib/reading";

import { ContextPanel } from "./ContextPanel";
import { UNFILED_ID, deleteWarning, librarySummary, libraryRows } from "./libraryRows";
import {
  LIBRARY_DRAG_TYPE,
  dropsBefore,
  planFolderDrop,
  planFolderReorder,
  planLogDrop,
  type LibraryDragPayload,
  type Reorder,
} from "./libraryDrag";

/** How long a single click waits to see whether it is the first half of a double click. */
const DOUBLE_CLICK_MS = 220;

/**
 * The synthetic id for "the top level", so the drop highlight has something to key on.
 *
 * Not a row and not a folder — the root is the *absence* of a folder, which is exactly why there
 * was no way to drag a log out of one until this existed.
 */
const ROOT_DROP_ID = "__root__";

/**
 * The library — where logs are filed (日本-207).
 *
 * **A sub-view of Read, not a rail destination.** The rail stays on 読 and the top bar becomes
 * `冊 書架` with a way back; the reading you were doing has not stopped, you have just opened a
 * drawer. Making it a fifth rail item would say the opposite.
 *
 * Search is **log and folder names only**. Sentence search across 6,000+ rows is a different query
 * and a deliberate non-goal here.
 */

interface LibraryProps {
  folders: ReadingFolder[];
  logs: ReadingLog[];
  /** So the log you are reading can carry its `open` pill — the only state badge in the list. */
  activeLogId: string | null;
  backLabel: string;
  onBack: () => void;
  onOpen: (logId: string) => void;
  onNewLog: () => void;
  onNewFolder: (name: string) => void;
  onRenameLog: (logId: string, name: string | null) => void;
  onFileLog: (logId: string, folderId: string | null) => void;
  onDeleteLog: (logId: string) => void;
  /** An unsaved log's destructive action — empties it, keeps it, and does not ask. */
  onClearLog: (logId: string) => void;
  /** Apply a drag: the new order, and the folder the dragged log now lives in. */
  onReorderLogs: (reorders: readonly Reorder[], moved?: { id: string; folderId: string | null }) => void;
  onReorderFolders: (reorders: readonly Reorder[]) => void;
  onRenameFolder: (folderId: string, name: string) => void;
  onDeleteFolder: (folderId: string) => void;
  isPhone?: boolean;
}

export function Library({
  folders,
  logs,
  activeLogId,
  backLabel,
  onBack,
  onOpen,
  onNewLog,
  onNewFolder,
  onRenameLog,
  onFileLog,
  onDeleteLog,
  onClearLog,
  onReorderLogs,
  onReorderFolders,
  onRenameFolder,
  onDeleteFolder,
  isPhone = false,
}: LibraryProps) {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  /** Which folder's `Context…` panel is open (日本-102). One at a time; null is closed. */
  const [contextFor, setContextFor] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; kind: "log" | "folder"; text: string } | null>(
    null,
  );
  /**
   * A folder being named before it exists.
   *
   * A **draft row** rather than create-then-rename: creating first and renaming after leaves a
   * phantom `新規フォルダ` behind every cancel, in a list whose whole job is to be tidy. It renders
   * beside `libraryRows()`'s output rather than inside it, so that pure function does not grow a
   * concept — a folder that is not a folder yet — which it has no business knowing about.
   */
  const [draftFolder, setDraftFolder] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const dialogs = useDialogs();
  const menu = useMenu();

  // `/` focuses search, `N` makes a log, `Esc` goes back — the three the design puts on the
  // toolbar. Guarded on a text field so typing a name does not trigger them.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA";
      if (event.key === "Escape") {
        event.preventDefault();
        // Innermost first. The row menus used to be the shallowest rung here; since 日本-318 they
        // are provider-owned and answer Escape themselves, in the CAPTURE phase — so this handler
        // never runs while one is open, and the rung would be dead code claiming otherwise. The
        // property it protected is unchanged: dismissing a menu must not also cost you the library.
        // The context panel is shallower still — it opens *from* a row menu, so leaving it must
        // not also leave the library. It closes itself on Escape, hence only the guard here.
        if (contextFor) return;
        if (renaming) setRenaming(null);
        else if (typing) searchRef.current?.blur();
        else onBack();
        return;
      }
      if (typing) return;
      if (event.key === "/") {
        event.preventDefault();
        searchRef.current?.focus();
      } else if (event.key === "n" || event.key === "N") {
        event.preventDefault();
        onNewLog();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack, onNewLog, renaming, contextFor]);

  /**
   * The two row menus, as item lists.
   *
   * Built here rather than inline at the `⋯` so the row's right-click offers exactly the same
   * items — one description, two gestures, and no way for them to drift.
   */
  const folderMenuItems = (row: { id: string; name: string; logCount: number }): MenuItemSpec[] => [
    {
      kind: "item",
      id: "rename",
      label: "名前を変更",
      onSelect: () => setRenaming({ id: row.id, kind: "folder", text: row.name }),
    },
    // **Context belongs to the folder, because a folder is already a work** (日本-102). This is one
    // of the two ways into the same panel; the other is `context ▾` on a translation, which is
    // where you reach for it when you are suspicious of an answer rather than setting a book up.
    { kind: "item", id: "context", label: "文脈…", onSelect: () => setContextFor(row.id) },
    { kind: "separator", id: "sep" },
    {
      kind: "item",
      id: "delete",
      label: "フォルダを削除",
      danger: true,
      onSelect: () => {
        // Its logs survive at the root, and saying so is the point: a folder row reading
        // `12 logs · 3,981 sentences` otherwise reads as a cascade.
        void dialogs
          .confirm({
            title: `フォルダ「${row.name}」を削除しますか？`,
            body:
              row.logCount === 0
                ? "ログは入っていません。"
                : "中のログは最上位へ移動します。中身は削除されません。",
            detail: row.logCount === 0 ? undefined : `ログ${row.logCount}`,
            confirmLabel: "フォルダを削除",
            tone: "danger",
          })
          .then((ok) => {
            if (ok) onDeleteFolder(row.id);
          });
      },
    },
  ];

  const logMenuItems = (row: { label: string; log: ReadingLog }): MenuItemSpec[] => [
    { kind: "item", id: "open", label: "開く", onSelect: () => onOpen(row.log.id) },
    {
      kind: "item",
      id: "rename",
      label: row.log.name === null ? "ログに名前を付ける" : "名前を変更",
      onSelect: () => setRenaming({ id: row.log.id, kind: "log", text: row.log.name ?? "" }),
    },
    {
      kind: "submenu",
      id: "file",
      label: "フォルダへ移動",
      empty: "フォルダがありません",
      items: [
        ...folders.map((folder): MenuItemSpec => ({
          kind: "item",
          id: `folder-${folder.id}`,
          label: folder.name,
          onSelect: () => onFileLog(row.log.id, folder.id),
        })),
        ...(row.log.folderId !== null
          ? [
              {
                kind: "item" as const,
                id: "folder-none",
                label: "（フォルダなし）",
                onSelect: () => onFileLog(row.log.id, null),
              },
            ]
          : []),
      ],
    },
    { kind: "separator", id: "sep" },
    // **An unsaved log's destructive action is Clear, not Delete** — and Clear does not ask,
    // because the scratchpad you empty twice a day must not. The two are genuinely different
    // operations rather than one behind a flag: Clear empties a log you are keeping, Delete
    // removes a log you named.
    row.log.name === null
      ? {
          kind: "item",
          id: "clear",
          label: "消去",
          danger: true,
          onSelect: () => onClearLog(row.log.id),
        }
      : {
          kind: "item",
          id: "delete",
          label: "削除",
          danger: true,
          onSelect: () => {
            // Confirmation-gated and it STATES what it takes. `deleteWarning` is why the sentence
            // carries real mined note ids rather than a running count, and it is reused here
            // rather than restated.
            void dialogs
              .confirm({
                title: `「${row.label}」を削除しますか？`,
                body: "ここから作成したカードはそのまま残ります。コレクションにあります。",
                detail: deleteWarning(row.log),
                confirmLabel: "ログを削除",
                tone: "danger",
              })
              .then((ok) => {
                if (ok) onDeleteLog(row.log.id);
              });
          },
        },
  ];

  /**
   * Open a row's menu at the pointer.
   *
   * Three things a row needs and a caller would forget. It **stops the event**, because a row is a
   * `role="button"` whose children sit inside it and `contextmenu` is one more phase that rule
   * covers — and because it lets an inner row beat an outer one. It **cancels the deferred single
   * click**: a right-click fires no `click`, so nothing else disarms the 220ms timer, and a left
   * click followed quickly by a right click would open the log behind the menu. And it **refuses
   * while a drag is live**, since one can already be in flight when the button goes down.
   */
  const openRowMenu = (event: React.MouseEvent, items: MenuItemSpec[]) => {
    if (dragging) return;
    if (
      !shouldOpenContextMenu(event.nativeEvent, {
        selectionInTarget: hasSelectionIn(event.currentTarget),
      })
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
    menu.openAt({ x: event.clientX, y: event.clientY }, items);
  };

  const rows = libraryRows(folders, logs, { collapsed, query });

  const toggle = (folderId: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });

  const commitRename = () => {
    if (!renaming) return;
    const text = renaming.text.trim();
    if (renaming.kind === "log") onRenameLog(renaming.id, text || null);
    else if (text) onRenameFolder(renaming.id, text);
    setRenaming(null);
  };

  /**
   * A single click that yields to a double click.
   *
   * Both gestures live on the same row now, and the single-click action is **opening a log** —
   * so firing it on the way into a rename would load a whole log's sentences and leave the library
   * behind you. Deferred, cancelled the moment `dblclick` arrives.
   */
  const clickTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
  }, []);
  const onSingleClick = (run: () => void) => (event: React.MouseEvent) => {
    if (event.detail > 1) return;
    if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
    clickTimer.current = window.setTimeout(run, DOUBLE_CLICK_MS);
  };
  const onDoubleClick = (run: () => void) => () => {
    if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
    run();
  };

  /**
   * The drag in flight, and where it would land.
   *
   * `over` carries the edge so the insertion rule is *shown* rather than guessed — a drop that
   * silently picked the other half of the row would be indistinguishable from one that ignored you.
   */
  const [dragging, setDragging] = useState<LibraryDragPayload | null>(null);
  /**
   * `mode` is what makes the indicator honest, because there are two different drops.
   *
   * **`edge`** is a reorder — a log over a log, a folder over a folder — and it has a position, so
   * it draws a rule on whichever half the pointer is in. **`into`** is filing a log into a folder,
   * which places it *last* and therefore has no before/after at all; it rings the whole row. An
   * edge line there was a quiet lie, implying a position the drop does not have.
   */
  const [over, setOver] = useState<{ id: string; before: boolean; mode: "edge" | "into" } | null>(
    null,
  );

  const startDrag = (payload: LibraryDragPayload) => (event: React.DragEvent) => {
    event.dataTransfer.setData(LIBRARY_DRAG_TYPE, JSON.stringify(payload));
    event.dataTransfer.effectAllowed = "move";
    setDragging(payload);
  };

  const endDrag = () => {
    setDragging(null);
    setOver(null);
  };

  /** A log being dragged that currently lives in a folder — the only case "the root" is a move. */
  const draggingFiledLog =
    dragging?.kind === "log" &&
    (logs.find((entry) => entry.id === dragging.id)?.folderId ?? null) !== null;

  /** Read the payload off the event, falling back to the in-flight one. */
  const payloadOf = (event: React.DragEvent): LibraryDragPayload | null => {
    const raw = event.dataTransfer.getData(LIBRARY_DRAG_TYPE);
    if (raw) {
      try {
        return JSON.parse(raw) as LibraryDragPayload;
      } catch {
        return null;
      }
    }
    // `getData` is deliberately empty during `dragover` in every browser — only `drop` may read it
    // — so the hover highlight has to come from state instead.
    return dragging;
  };

  const dropOnLog = (targetLogId: string) => (event: React.DragEvent) => {
    // The list container is a drop target too — it is how a log leaves its folder — so a drop that
    // landed on a ROW must stop here, or every row drop would also be read as "move to the root".
    event.stopPropagation();
    const payload = payloadOf(event);
    endDrag();
    if (payload?.kind !== "log") return;
    event.preventDefault();
    const before = dropsBefore(
      event.clientY,
      (event.currentTarget as HTMLElement).getBoundingClientRect(),
    );
    const plan = planLogDrop(logs, payload.id, targetLogId, before);
    if (!plan) return;
    onReorderLogs(plan.reorders, { id: payload.id, folderId: plan.folderId ?? null });
  };

  const dropOnFolder = (folderId: string | null) => (event: React.DragEvent) => {
    event.stopPropagation();
    const payload = payloadOf(event);
    endDrag();
    if (!payload) return;
    event.preventDefault();
    if (payload.kind === "folder") {
      // Folders reorder among themselves. `Unfiled` is synthetic and is not a folder you can move
      // one into or past, so it is not a target for this.
      if (folderId === null) return;
      const plan = planFolderReorder(
        folders,
        payload.id,
        folderId,
        dropsBefore(event.clientY, (event.currentTarget as HTMLElement).getBoundingClientRect()),
      );
      if (plan) onReorderFolders(plan.reorders);
      return;
    }
    const plan = planFolderDrop(logs, payload.id, folderId);
    if (!plan) return;
    onReorderLogs(plan.reorders, { id: payload.id, folderId: plan.folderId ?? null });
  };

  const hoverHandlers = (
    id: string,
    accepts: (kind: "log" | "folder") => boolean,
    /** `into` when this row is a folder and the thing being dragged is a log — see `over`. */
    modeFor: (kind: "log" | "folder") => "edge" | "into" = () => "edge",
  ) => ({
    onDragOver: (event: React.DragEvent) => {
      if (!event.dataTransfer.types.includes(LIBRARY_DRAG_TYPE)) return;
      const kind = dragging?.kind;
      if (kind && !accepts(kind)) return;
      // **stopPropagation, and this is not defensive.** The list container is a dragover target too
      // — it is how a log reaches the top level — so without this every hover on a row bubbled up
      // and the container overwrote `over` with the root marker on the SAME event. The row's own
      // indicator was set and erased in one tick, which is why every insertion line disappeared
      // while the drops themselves kept working.
      event.stopPropagation();
      // Required, and on every dragover — a target that does not preventDefault is not a target.
      event.preventDefault();
      setOver({
        id,
        before: dropsBefore(
          event.clientY,
          (event.currentTarget as HTMLElement).getBoundingClientRect(),
        ),
        mode: kind ? modeFor(kind) : "edge",
      });
    },
    onDragLeave: () => setOver((current) => (current?.id === id ? null : current)),
  });

  /**
   * What the row shows while a drop is hovering it.
   *
   * Returned as **parts**, because the log row already carries an `inset 3px 0 0 action` bar for
   * the log you are reading and both live on `boxShadow` — spreading one style object over the
   * other made the second silently win, so the active bar vanished under the insertion line.
   */
  const insertionShadow = (id: string): string | null => {
    if (over?.id !== id) return null;
    if (over.mode === "into") return "inset 0 0 0 2px var(--accent)";
    return over.before ? "inset 0 2px 0 var(--accent)" : "inset 0 -2px 0 var(--accent)";
  };

  const insertionBackground = (id: string): string | undefined =>
    over?.id === id && over.mode === "into" ? "var(--accent-tint)" : undefined;

  return (
    <>
      <TopBar title="冊 書架" subtitle={librarySummary(folders, logs)}>
        <button
          type="button"
          onClick={onBack}
          className="text-secondary text-ink-3 hover:text-ink"
        >
          ← {backLabel}へ戻る
        </button>
        {!isPhone && (
          <>
            <TopBarDivider />
            <span className="font-mono text-meta text-ink-4">Esc</span>
          </>
        )}
      </TopBar>

      <div className="flex h-[44px] shrink-0 items-center gap-[10px] border-b border-line bg-panel px-[18px] box-border">
        {/* **The search field shrinks; the buttons do not.** It was a fixed `w-[250px]` beside two
            buttons with no `shrink-0`, which at 390px leaves ~84px for two labels needing ~190 —
            so the flex row squeezed the BUTTONS instead, and `+ 新規ログ` wrapped to three lines
            inside a fixed 28px box and spilled out of it. Alex caught it on a real phone.
            Nothing overflowed the page, so the standing horizontal-scroll assertion passed
            throughout; the failure is entirely inside two 28px boxes. */}
        <div className="relative min-w-0 flex-1 sm:max-w-[250px]">
          <input
            ref={searchRef}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder="ログを検索"
            aria-label="名前でログを検索"
            className="h-[28px] w-full rounded-[var(--r-2)] border border-line bg-void px-[9px] pr-[26px] text-[14px] outline-none focus:border-accent"
          />
          {!isPhone && (
            <span className="pointer-events-none absolute right-[8px] top-[6px] font-mono text-[14px] text-ink-4">
              /
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onNewLog}
          data-check="read.new-log"
          className="flex h-[28px] shrink-0 items-center gap-[6px] whitespace-nowrap rounded-[var(--r-2)] bg-[color:var(--accent)] px-[10px] text-[14px] font-semibold text-white"
        >
          + 新規ログ
          {!isPhone && <span className="font-mono text-[14px] opacity-70">N</span>}
        </button>
        <button
          type="button"
          onClick={() => setDraftFolder(true)}
          className="flex h-[28px] shrink-0 items-center whitespace-nowrap rounded-[var(--r-2)] border border-line px-[10px] text-[14px] text-ink-2 hover:bg-[color:var(--tab-hover)]"
        >
          + 新規フォルダ
        </button>
      </div>

      {/**
        * **The list itself is the drop target for "the top level".**
        *
        * Every other destination is a row, and the root is not a row — so before this there was no
        * way to drag a log OUT of a folder at all, which is what Alex hit. Row drops stopPropagation
        * above, so this only ever sees a drop that landed on empty space: above the first row, below
        * the last, or in the gutters beside them.
        */}
      <div
        onDragOver={(event) => {
          if (!event.dataTransfer.types.includes(LIBRARY_DRAG_TYPE)) return;
          if (dragging?.kind !== "log") return;
          event.preventDefault();
          setOver({ id: ROOT_DROP_ID, before: false, mode: "into" });
        }}
        onDrop={dropOnFolder(null)}
        className="flex min-h-0 flex-1 flex-col gap-[3px] overflow-auto px-[18px] py-[12px]">
        {rows.length === 0 && !draftFolder && (
          <p className="text-secondary text-ink-3">
            {query ? `「${query}」に一致するログはありません。` : "ログがありません。"}
          </p>
        )}

        {/* The folder being named, drawn as the folder row it is about to become so you can see
            where it will land. Enter creates it; Escape or an empty blur discards it, leaving
            nothing behind — which is the whole reason this is a draft rather than a real folder
            renamed after the fact. */}
        {draftFolder && (
          <div className="flex h-[34px] shrink-0 items-center gap-[9px] rounded-[var(--r-2)] bg-[color:var(--sunken)] px-[10px] box-border">
            <span aria-hidden className="w-[9px] text-[14px] text-ink-4">
              ▾
            </span>
            <input
              autoFocus
              placeholder="フォルダ名 — サクラノ詩のような作品名"
              aria-label="新しいフォルダ名"
              onKeyDown={(event) => {
                // Stopped, because the library's own window keydown reads Escape as "leave the
                // library" and `/` and `N` as shortcuts — all three are things you may type here.
                event.stopPropagation();
                // A Japanese IME commits a conversion with Enter, and that keystroke arrives here
                // like any other — so without this, accepting the kanji in サクラノ詩 creates the
                // folder under the half-typed kana. The placeholder is that very example.
                // `isComposing` is the modern signal; keyCode 229 covers older WebViews, which is
                // what the desktop shell is. Same guard `AskPanel` and `TagChips` carry.
                if (event.nativeEvent.isComposing || event.keyCode === 229) return;
                if (event.key === "Enter") {
                  const name = event.currentTarget.value.trim();
                  setDraftFolder(false);
                  if (name) onNewFolder(name);
                } else if (event.key === "Escape") {
                  setDraftFolder(false);
                }
              }}
              onBlur={(event) => {
                const name = event.currentTarget.value.trim();
                setDraftFolder(false);
                if (name) onNewFolder(name);
              }}
              className="min-w-0 flex-1 rounded-[var(--r-2)] border border-line-strong bg-void px-[6px] py-[2px] text-[15px] text-ink"
            />
          </div>
        )}

        {rows.map((row) =>
          row.kind === "folder" ? (
            <div
              key={`folder-${row.id}`}
              // **Click and double-click belong to the ROW**, not to the caret or the name — the
              // gripper and the `⋯` are the only two exceptions and they stopPropagation. A folder
              // is a disclosure, so a single click toggles it and a double renames it.
              role="button"
              tabIndex={0}
              // Explicit, because a role=button computes its name from its DESCENDANTS otherwise —
              // gripper, caret, name and counts concatenated into one unreadable string that also
              // collides with the `⋯` button's own label.
              aria-label={`${row.name}を${collapsed.has(row.id) ? "開く" : "閉じる"}`}
              onClick={onSingleClick(() => toggle(row.id))}
              onDoubleClick={onDoubleClick(() => {
                if (row.id === UNFILED_ID) return;
                setRenaming({ id: row.id, kind: "folder", text: row.name });
              })}
              // `未整理` is synthetic and has no menu, here as at the `⋯`.
              onContextMenu={
                row.id === UNFILED_ID
                  ? undefined
                  : (event) => openRowMenu(event, folderMenuItems(row))
              }
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                toggle(row.id);
              }}
              {...(row.id === UNFILED_ID
                ? {}
                : hoverHandlers(row.id, () => true, (kind) => (kind === "log" ? "into" : "edge")))}
              onDrop={row.id === UNFILED_ID ? undefined : dropOnFolder(row.id)}
              className="flex h-[34px] shrink-0 cursor-default items-center gap-[9px] rounded-[var(--r-2)] bg-[color:var(--sunken)] px-[10px] box-border"
              style={
                row.id === UNFILED_ID
                  ? undefined
                  : {
                      boxShadow: insertionShadow(row.id) ?? undefined,
                      background: insertionBackground(row.id),
                    }
              }
            >
              {/* `未整理` is synthetic — "these logs have no name" — so there is nothing to drag. */}
              {row.id === UNFILED_ID ? (
                <span className="w-[11px] shrink-0" />
              ) : (
                <span
                  draggable
                  onDragStart={startDrag({ kind: "folder", id: row.id })}
                  onDragEnd={endDrag}
                  onClick={(event) => event.stopPropagation()}
                  onDoubleClick={(event) => event.stopPropagation()}
                  title="ドラッグで並べ替え"
                  className="shrink-0 cursor-grab select-none text-[14px] leading-none text-ink-4 hover:text-ink-3 active:cursor-grabbing"
                >
                  ⣿
                </span>
              )}
              <span aria-hidden className="text-[14px] text-ink-3">
                {collapsed.has(row.id) ? "▸" : "▾"}
              </span>
              {renaming?.kind === "folder" && renaming.id === row.id ? (
                <input
                  autoFocus
                  // Select what is there — see `TabStrip`'s copy of this line for the argument.
                  onFocus={(event) => event.currentTarget.select()}
                  value={renaming.text}
                  onChange={(event) =>
                    setRenaming({ ...renaming, text: event.currentTarget.value })
                  }
                  onBlur={commitRename}
                  // The field lives INSIDE the row, so its own clicks would re-fire the row's
                  // toggle and rename — double-clicking a word to select it is the obvious way in.
                  onClick={(event) => event.stopPropagation()}
                  onDoubleClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    // **stopPropagation, always.** The field sits INSIDE a `role="button"` row that
                    // treats Enter as "open this log" / "toggle this folder" — so committing a
                    // rename with Enter also fired the row's action, closing the library on the
                    // keystroke that was supposed to save a name.
                    event.stopPropagation();
                    if (event.key === "Enter") commitRename();
                    else if (event.key === "Escape") setRenaming(null);
                  }}
                  className="min-w-0 flex-1 rounded-[var(--r-2)] border border-accent bg-panel px-[6px] py-[2px] font-jp text-[15px] outline-none"
                />
              ) : (
                <span className="min-w-0 flex-1 truncate font-jp text-[15px] font-semibold text-ink">
                  {row.name}
                </span>
              )}
              <span className="shrink-0 font-mono text-[14px] text-ink-3">
                ログ{row.logCount} · {row.sentenceCount.toLocaleString()}文
              </span>
              {/* `未整理` is synthetic — it is "these logs have no name", not a row you can
                  rename or delete. Giving it a menu would offer both. */}
              {row.id !== UNFILED_ID && (
                <div className="relative shrink-0">
                  <button
                    type="button"
                    // The `⋯` is the row's second exception: it opens the menu and must not also
                    // collapse the folder underneath it.
                    onClick={(event) => {
                      event.stopPropagation();
                      menu.openFrom(
                        event.currentTarget,
                        folderMenuItems(row),
                        `f:${row.id}`,
                        "end",
                      );
                    }}
                    onDoubleClick={(event) => event.stopPropagation()}
                    aria-label={`${row.name}の操作`}
                    aria-haspopup="menu"
                    aria-expanded={menu.openKey === `f:${row.id}`}
                    className="px-[5px] text-[14px] text-ink-4 hover:text-ink-2"
                  >
                    ⋯
                  </button>
                  {contextFor === row.id && (
                    <ContextPanel
                      scope={{ folderId: row.id }}
                      onClose={() => setContextFor(null)}
                      // Right-aligned: the `⋯` sits at the end of the row, so a 400px panel hung
                      // from its left edge would run off a 1147px window.
                      className="absolute right-0 top-[22px]"
                    />
                  )}
                </div>
              )}
            </div>
          ) : (
            <div
              key={`log-${row.log.id}`}
              // **The whole row is the click target.** Single click opens the log, double click
              // renames it in place — anywhere: the name, the folder column, the count, the gaps
              // between them. The gripper and the `⋯` are the only two exceptions, and both
              // stopPropagation.
              role="button"
              tabIndex={0}
              // Explicit, for the reason the folder row's is — see above.
              aria-label={`${row.label}を開く`}
              data-check="library.log"
              /* Whether the log is FILED, as data. Naming a log is what files it, so `name === null`
                 is the unsaved state — and a check cannot read that off the label, which is the
                 generated `新しいログ 7`. */
              data-named={row.log.name ? "true" : "false"}
              onClick={onSingleClick(() => onOpen(row.log.id))}
              onDoubleClick={onDoubleClick(() =>
                setRenaming({ id: row.log.id, kind: "log", text: row.log.name ?? "" }),
              )}
              onContextMenu={(event) => openRowMenu(event, logMenuItems(row))}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                onOpen(row.log.id);
              }}
              {...hoverHandlers(row.log.id, (kind) => kind === "log")}
              onDrop={dropOnLog(row.log.id)}
              className="flex h-[38px] shrink-0 cursor-default items-center gap-[10px] rounded-[var(--r-2)] border border-line bg-panel px-[10px] box-border hover:border-[color:var(--line-strong)]"
              style={{
                marginLeft: row.indented ? 20 : 0,
                // JOINED, not spread over each other. The one state signal in the list is the
                // `inset 3px 0 0 action` bar on the log you are reading — everything else is just
                // filed — and it shares `boxShadow` with the insertion edge, so a second style
                // object silently replaced it. (The `open` chip that used to say the same thing
                // beside it is gone, Alex 5 Aug 2026.)
                boxShadow:
                  [
                    row.log.id === activeLogId ? "inset 3px 0 0 var(--accent)" : null,
                    insertionShadow(row.log.id),
                  ]
                    .filter(Boolean)
                    .join(", ") || undefined,
              }}
            >
              <span
                draggable
                onDragStart={startDrag({ kind: "log", id: row.log.id })}
                onDragEnd={endDrag}
                // The gripper is the drag handle and nothing else. Without these the row's own
                // click would open the log at the end of every drag.
                onClick={(event) => event.stopPropagation()}
                onDoubleClick={(event) => event.stopPropagation()}
                title="ドラッグで並べ替え、フォルダへ入れると整理されます"
                className="shrink-0 cursor-grab select-none text-[14px] leading-none text-ink-4 hover:text-ink-3 active:cursor-grabbing"
              >
                ⣿
              </span>
              {renaming?.kind === "log" && renaming.id === row.log.id ? (
                <input
                  autoFocus
                  // Select what is there — see `TabStrip`'s copy of this line for the argument.
                  onFocus={(event) => event.currentTarget.select()}
                  value={renaming.text}
                  onChange={(event) =>
                    setRenaming({ ...renaming, text: event.currentTarget.value })
                  }
                  onBlur={commitRename}
                  onClick={(event) => event.stopPropagation()}
                  onDoubleClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    // **stopPropagation, always.** The field sits INSIDE a `role="button"` row that
                    // treats Enter as "open this log" / "toggle this folder" — so committing a
                    // rename with Enter also fired the row's action, closing the library on the
                    // keystroke that was supposed to save a name.
                    event.stopPropagation();
                    if (event.key === "Enter") commitRename();
                    else if (event.key === "Escape") setRenaming(null);
                  }}
                  placeholder="名前を付けると整理されます"
                  className="w-[210px] shrink-0 rounded-[var(--r-2)] border border-accent bg-void px-[6px] py-[2px] font-jp text-[15px] outline-none"
                />
              ) : (
                // A span, not a button: the ROW is the target now, and a nested button would
                // swallow the click before the row's handler could decide whether it was one.
                <span
                  className="w-[210px] shrink-0 truncate font-jp text-[15px] text-ink"
                  style={row.log.name === null ? { fontStyle: "italic", color: "var(--ink-4)" } : undefined}
                >
                  {row.label}
                </span>
              )}
              <span className="w-[150px] shrink-0 truncate text-[14px] text-ink-3">
                {folders.find((folder) => folder.id === row.log.folderId)?.name ?? ""}
              </span>
              <span className="flex-1" />
              <span className="w-[96px] shrink-0 text-right font-mono text-[14px] text-ink-2">
                {row.log.sentenceCount.toLocaleString()}
              </span>
              <span className="shrink-0 text-[14px] text-ink-4">文</span>
              <div className="relative shrink-0">
                <button
                  type="button"
                  // The row's other exception: it opens the menu and must not also open the log.
                  onClick={(event) => {
                    event.stopPropagation();
                    menu.openFrom(event.currentTarget, logMenuItems(row), `l:${row.log.id}`, "end");
                  }}
                  onDoubleClick={(event) => event.stopPropagation()}
                  aria-label={`${row.label}の操作`}
                  aria-haspopup="menu"
                  aria-expanded={menu.openKey === `l:${row.log.id}`}
                  className="px-[5px] text-[14px] text-ink-4 hover:text-ink-2"
                >
                  ⋯
                </button>
              </div>
            </div>
          ),
        )}

        {/* Shown only while a log that is IN a folder is being dragged — the one case where "the
            top level" is a destination you cannot otherwise point at. Empty space is a target you
            have to be told about, and the previous version told nobody. */}
        {draggingFiledLog && (
          <div
            onDragOver={(event) => {
              event.preventDefault();
              setOver({ id: ROOT_DROP_ID, before: false, mode: "into" });
            }}
            onDrop={dropOnFolder(null)}
            className="flex h-[34px] shrink-0 items-center justify-center rounded-[var(--r-2)] border border-dashed text-[14px]"
            style={{
              borderColor: over?.id === ROOT_DROP_ID ? "var(--accent)" : "var(--line-strong)",
              color: over?.id === ROOT_DROP_ID ? "var(--accent)" : "var(--ink-4)",
              background: over?.id === ROOT_DROP_ID ? "var(--accent-tint)" : undefined,
            }}
          >
            最上位へ移動
          </div>
        )}
      </div>
    </>
  );
}

